import { heroUrl } from "../../lib/api";
import { QrMatrix, useQrCode } from "../QrMatrix";

const SCULPTORS = { hunyuan3d_pro: "Hunyuan3D 3.1 Pro", hunyuan3d: "Hunyuan3D", trellis: "Trellis", "3daistudio": "Tripo 3.2" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const Fact = ({ label, value, testid }) => (
  <div className="flex justify-between gap-2 border-b border-dotted border-black/20 py-[0.6mm]">
    <span className="uppercase tracking-widest text-[6.5pt] text-black/55">{label}</span>
    <span data-testid={testid} className="font-semibold text-[7.5pt] text-right truncate">{value}</span>
  </div>
);

// Tilted rubber stamp over the render corner once a print of this duck has shipped (with the count when more than one did).
const PrintedStamp = ({ date, count = 1, mono }) => (
  <div
    data-testid="passport-printed-stamp"
    className="absolute z-10 rounded-[1.5mm] border-[0.6mm] border-double text-center uppercase leading-none"
    style={{ left: "15mm", top: "45mm", padding: "1.4mm 2.2mm", transform: "rotate(-12deg)", fontFamily: "Space Grotesk, sans-serif", mixBlendMode: "multiply", opacity: 0.88, borderColor: mono ? "#121417" : "#c8102e", color: mono ? "#121417" : "#c8102e" }}
  >
    <div className="font-bold text-[7pt] tracking-[0.12em]">Printed by</div>
    <div className="font-extrabold text-[8.5pt] tracking-wide">Zero Ducks Given</div>
    <div data-testid="passport-stamp-date" className="font-mono2 text-[6pt] mt-[0.6mm] tracking-widest">{count > 1 ? `×${count} shipped · ` : ""}{fmtDate(date)}</div>
  </div>
);

// A6 (105 x 148 mm) printable "Duck Passport": render, name + story, filament slots, print facts and a QR to the spinning viewer.
// `mono` = high-contrast black & white for direct-thermal label printers (Rollo).
export const PassportCard = ({ duck, height, shipped, mono = false }) => {
  const story = duck.story || {};
  const name = duck.name || story.name || "A Duckify3D figurine";
  const palette = duck.palette || [];
  const qr = useQrCode(duck.id);

  return (
    <article
      data-testid="passport-card"
      className="print-exact relative text-[#121417] overflow-hidden flex flex-col"
      style={{ width: "105mm", height: "148mm", padding: "4mm", gap: "2.5mm", fontFamily: "Outfit, sans-serif", background: mono ? "#fff" : "#FFFDF5" }}
    >
      {shipped && <PrintedStamp date={shipped.at} count={shipped.shipped} mono={mono} />}
      <header
        className="flex items-center justify-between rounded-[2mm] px-[3mm]"
        style={{ height: "9mm", background: mono ? "#121417" : "#FFC700", color: mono ? "#fff" : "#121417" }}
      >
        <span className="font-heading font-bold text-[9pt] tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          DUCK PASSPORT
        </span>
        <span className={`text-[6.5pt] font-mono2 uppercase tracking-widest ${mono ? "text-white/80" : "text-black/70"}`}>
          Duckify3D · <span data-testid="passport-version">Rev {duck.version || 1}</span>
        </span>
      </header>

      <section className="flex" style={{ gap: "3mm", height: "42mm" }}>
        <img
          data-testid="passport-render"
          src={duck.build_status === "ready" ? heroUrl(duck.id) : duck.render}
          alt="duck render"
          className="rounded-[2.5mm] object-cover bg-[#eee]"
          style={{ width: "42mm", height: "42mm", filter: mono ? "grayscale(1) contrast(1.35)" : "none" }}
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <h1 data-testid="passport-name" className="font-bold leading-[1.05] text-[13pt] tracking-tight line-clamp-2" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {name}
          </h1>
          <div className="mt-auto">
            <Fact label="Hatched" value={fmtDate(duck.built_at || duck.created_at)} testid="passport-date" />
            <Fact label="Height" value={`${height} mm`} testid="passport-height" />
            <Fact label="Mesh" value={duck.mesh_faces ? `${Math.round(duck.mesh_faces / 1000)}k tris` : "—"} />
            <Fact label="Sculptor" value={SCULPTORS[duck.sculptor] || "Trellis"} />
          </div>
        </div>
      </section>

      <p data-testid="passport-story" className="text-[7.5pt] leading-[1.35] text-black/80 line-clamp-5">
        {story.description || "A one-of-a-kind rubber-duck figurine, rendered by AI from a real portrait and sculpted into a printable 3D model."}
      </p>

      <section className="flex-1 min-h-0">
        <p className="text-[6.5pt] font-mono2 uppercase tracking-widest text-black/55 mb-[1mm]">Filament slots · Bambu AMS ({palette.length})</p>
        <ul data-testid="passport-filaments" className="grid" style={{ gap: "1.4mm 2.5mm", gridTemplateColumns: `repeat(${palette.length > 8 ? 4 : 2}, minmax(0, 1fr))` }}>
          {palette.map((p) => (
            <li key={p.slot} className="flex items-center min-w-0" style={{ gap: "1.5mm" }}>
              <span
                className="shrink-0 rounded-full grid place-items-center text-[6pt] font-bold border border-black/15"
                style={
                  mono
                    ? { width: "5.5mm", height: "5.5mm", background: "#fff", color: "#121417", border: "0.35mm solid #121417" }
                    : { width: "5.5mm", height: "5.5mm", background: p.hex, color: luma(p.hex) > 0.6 ? "#121417" : "#fff" }
                }
              >
                {p.slot}
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-[6.5pt] font-semibold truncate">{p.name}</span>
                <span className="block text-[5.5pt] font-mono2 text-black/55 truncate">{p.line ? `${p.line} · ` : ""}{p.hex}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <footer className="flex items-center" style={{ gap: "3mm" }}>
        <div className="rounded-[1.5mm] bg-white p-[1mm]" style={{ border: mono ? "0.4mm solid #121417" : "0.2mm solid rgba(0,0,0,0.1)" }}>
          <QrMatrix testid="passport-qr" qr={qr} size={mono ? "26mm" : "22mm"} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-[8pt] leading-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Scan me — spin, download and reorder</p>
          <p data-testid="passport-url" className="text-[5.5pt] font-mono2 text-black/60 break-all leading-tight mt-[0.6mm]">{qr?.url || ""}</p>
          <p className="text-[6pt] text-black/60 mt-[1mm] leading-tight" data-testid="passport-print-facts">
            Printed from a Bambu Studio multi-color .3MF · tree-slim supports · flat base
            {duck.overhang?.needs_supports ? " · strong overhangs: print with tree supports" : ""}
          </p>
        </div>
        <img src="/zdg-logo.png" alt="" className="shrink-0 object-contain" style={{ height: "9mm" }} />
      </footer>
    </article>
  );
};

const luma = (hex) => {
  const h = (hex || "#000").replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2) || "0", 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
